<template>
  <Card cardClasses="shadow-sm border-0">
    <!-- Compact Header -->
    <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
      <div class="d-flex align-items-center gap-2">
        <button class="btn btn-outline-dark btn-sm" @click="$router.go(-1)">
          <i class="bi bi-arrow-left me-1"></i> Back
        </button>
        <div>
          <h5 class="m-0 text-primary d-flex align-items-center">
            <i class="bi bi-clock-history me-2"></i>{{ productName }}
          </h5>
          <span class="text-muted small">Stock Ledger & Movement History</span>
        </div>
      </div>

      <!-- View Mode & Actions -->
      <div class="d-flex align-items-center gap-2">
        <div class="btn-group btn-group-sm" role="group">
          <button
            type="button"
            class="btn"
            :class="viewMode === 'table' ? 'btn-primary' : 'btn-outline-primary'"
            @click="viewMode = 'table'"
            title="Compact Table View"
          >
            <i class="bi bi-table me-1"></i> Table
          </button>
          <button
            type="button"
            class="btn"
            :class="viewMode === 'timeline' ? 'btn-primary' : 'btn-outline-primary'"
            @click="viewMode = 'timeline'"
            title="Timeline View"
          >
            <i class="bi bi-clock-history me-1"></i> Timeline
          </button>
        </div>
        <button class="btn btn-sm btn-outline-secondary" @click="loadHistory" title="Refresh">
          <i class="bi bi-arrow-clockwise"></i>
        </button>
      </div>
    </div>

    <!-- Compact Summary Stat Bar -->
    <div v-if="!loading && summary" class="row g-2 mb-3">
      <div class="col-6 col-md-3">
        <div class="p-2 border rounded bg-light d-flex align-items-center justify-content-between">
          <div>
            <span class="text-muted small d-block">Total In</span>
            <strong class="text-success fs-6">+{{ summary.total_in }}</strong>
          </div>
          <i class="bi bi-arrow-down-left-circle text-success fs-4 opacity-50"></i>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="p-2 border rounded bg-light d-flex align-items-center justify-content-between">
          <div>
            <span class="text-muted small d-block">Total Out</span>
            <strong class="text-danger fs-6">-{{ summary.total_out }}</strong>
          </div>
          <i class="bi bi-arrow-up-right-circle text-danger fs-4 opacity-50"></i>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="p-2 border rounded bg-light d-flex align-items-center justify-content-between">
          <div>
            <span class="text-muted small d-block">Current Stock</span>
            <strong class="text-primary fs-6">{{ summary.current_balance }}</strong>
          </div>
          <i class="bi bi-box-seam text-primary fs-4 opacity-50"></i>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="p-2 border rounded bg-light d-flex align-items-center justify-content-between">
          <div>
            <span class="text-muted small d-block">Total Value</span>
            <strong class="text-dark fs-6">{{ formatCurrency(summary.total_value) }}</strong>
          </div>
          <i class="bi bi-cash-stack text-dark fs-4 opacity-50"></i>
        </div>
      </div>
    </div>

    <!-- Filter & Search Toolbar -->
    <div class="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3 p-2 bg-light rounded border">
      <div class="d-flex align-items-center gap-2 flex-grow-1" style="max-width: 400px;">
        <div class="input-group input-group-sm">
          <span class="input-group-text bg-white"><i class="bi bi-search"></i></span>
          <input
            v-model="searchQuery"
            type="text"
            class="form-control"
            placeholder="Search reference, notes..."
          />
          <button v-if="searchQuery" class="btn btn-outline-secondary" @click="searchQuery = ''" type="button">
            <i class="bi bi-x"></i>
          </button>
        </div>
      </div>

      <div class="d-flex align-items-center gap-2">
        <select v-model="typeFilter" class="form-select form-select-sm w-auto">
          <option value="">All Movement Types</option>
          <option value="sale">Sales</option>
          <option value="purchase">Purchases</option>
          <option value="direct_purchase">Direct Purchases</option>
          <option value="sale_return">Sale Returns</option>
          <option value="supplier_return">Supplier Returns</option>
          <option value="store_transfer">Store Transfers</option>
          <option value="transfer">Warehouse Transfers</option>
        </select>

        <span class="badge bg-secondary rounded-pill small">
          {{ filteredHistory.length }} event{{ filteredHistory.length !== 1 ? 's' : '' }}
        </span>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
      <span class="text-muted small">Loading movement history...</span>
    </div>

    <!-- Empty State -->
    <div v-else-if="filteredHistory.length === 0" class="text-center py-5 text-muted">
      <i class="bi bi-inbox display-6 d-block mb-2 opacity-25"></i>
      <p class="mb-0 small">No product movements matching filter criteria.</p>
    </div>

    <!-- 1. COMPACT TABLE VIEW (DEFAULT) -->
    <div v-else-if="viewMode === 'table'" class="compact-ledger">
      <div class="table-responsive border rounded mb-3">
        <table class="table table-sm table-hover align-middle mb-0">
          <thead class="table-light small">
            <tr>
              <th style="width: 140px;">Date</th>
              <th style="width: 130px;">Movement Type</th>
              <th>Reference / Doc</th>
              <th class="text-end" style="width: 90px;">Qty</th>
              <th class="text-end" style="width: 110px;">Unit Price</th>
              <th class="text-end" style="width: 110px;">Balance After</th>
              <th>Details / Breakdown</th>
            </tr>
          </thead>
          <tbody class="small">
            <tr v-for="(entry, index) in paginatedHistory" :key="index">
              <td class="text-nowrap text-muted">{{ formatCompactDate(entry.date) }}</td>
              <td>
                <span class="badge" :class="getTypeBadgeClass(entry.type)">
                  <i :class="getTypeIcon(entry.type)" class="me-1"></i>
                  {{ getTypeLabel(entry.type) }}
                </span>
              </td>
              <td>
                <span v-if="entry.reference_number || entry.reference" class="fw-semibold text-primary">
                  {{ entry.reference_number || entry.reference }}
                </span>
                <span v-else class="text-muted">-</span>
              </td>
              <td class="text-end fw-bold" :class="entry.quantity < 0 ? 'text-danger' : 'text-success'">
                {{ entry.quantity > 0 ? '+' : '' }}{{ entry.quantity }}
              </td>
              <td class="text-end text-muted">{{ formatCurrency(entry.unit_price) }}</td>
              <td class="text-end fw-bold text-dark">{{ entry.running_balance }}</td>
              <td>
                <div v-if="entry.type === 'sale' && entry.sales" class="small">
                  <span class="badge bg-light text-dark border me-1">{{ entry.count || 1 }} sales</span>
                  <span class="text-muted">Avg: {{ formatCurrency(entry.sales.average_price) }}</span>
                  <span class="text-muted ms-1">| Rev: {{ formatCurrency(entry.sales.total_revenue) }}</span>
                </div>
                <div v-else-if="entry.description || entry.notes" class="text-muted text-truncate" style="max-width: 250px;">
                  {{ entry.description || entry.notes }}
                </div>
                <span v-else class="text-muted">-</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination Controls -->
      <div class="d-flex flex-wrap align-items-center justify-content-between gap-2 pt-1">
        <div class="d-flex align-items-center gap-2">
          <span class="small text-muted">Rows per page:</span>
          <select v-model.number="perPage" class="form-select form-select-sm w-auto">
            <option :value="10">10</option>
            <option :value="25">25</option>
            <option :value="50">50</option>
            <option :value="100">100</option>
          </select>
          <span class="small text-muted ms-2">
            Showing {{ (currentPage - 1) * perPage + 1 }} - {{ Math.min(currentPage * perPage, filteredHistory.length) }} of {{ filteredHistory.length }}
          </span>
        </div>

        <div v-if="totalPages > 1">
          <Pagination
            v-model="currentPage"
            :pages="totalPages"
            :threshold="2"
          >
            <template #previous>Prev</template>
            <template #next>Next</template>
          </Pagination>
        </div>
      </div>
    </div>

    <!-- 2. COMPACT TIMELINE VIEW -->
    <div v-else-if="viewMode === 'timeline'" class="compact-timeline">
      <div v-for="(entry, index) in paginatedHistory" :key="index" class="timeline-entry d-flex align-items-start mb-2">
        <!-- Compact Marker -->
        <div class="timeline-dot me-2 mt-1" :class="getTypeClass(entry.type)">
          <i :class="getTypeIcon(entry.type)"></i>
        </div>

        <!-- Compact Content Box -->
        <div class="flex-grow-1 p-2 border rounded bg-white shadow-2xs">
          <div class="d-flex flex-wrap justify-content-between align-items-center gap-1 mb-1">
            <div class="d-flex align-items-center gap-2">
              <span class="badge" :class="getTypeBadgeClass(entry.type)">
                {{ getTypeLabel(entry.type) }}
              </span>
              <span class="text-muted small">{{ formatCompactDate(entry.date) }}</span>
              <span v-if="entry.reference_number || entry.reference" class="small fw-semibold text-primary">
                #{{ entry.reference_number || entry.reference }}
              </span>
            </div>
            <div class="d-flex align-items-center gap-3">
              <span class="small">
                Qty: <strong :class="entry.quantity < 0 ? 'text-danger' : 'text-success'">
                  {{ entry.quantity > 0 ? '+' : '' }}{{ entry.quantity }}
                </strong>
              </span>
              <span class="small text-muted">
                Price: {{ formatCurrency(entry.unit_price) }}
              </span>
              <span class="small">
                Balance: <strong class="text-primary">{{ entry.running_balance }}</strong>
              </span>
            </div>
          </div>

          <div v-if="entry.type === 'sale' && entry.sales" class="small text-muted bg-light px-2 py-1 rounded d-flex gap-3 mt-1">
            <span>{{ entry.count || 1 }} sales</span>
            <span>Total Qty: {{ entry.sales.total_quantity }}</span>
            <span>Avg Price: {{ formatCurrency(entry.sales.average_price) }}</span>
            <span>Revenue: {{ formatCurrency(entry.sales.total_revenue) }}</span>
          </div>

          <div v-else-if="entry.description || entry.notes" class="small text-muted mt-1">
            {{ entry.description || entry.notes }}
          </div>
        </div>
      </div>

      <!-- Pagination for Timeline -->
      <div class="d-flex flex-wrap align-items-center justify-content-between gap-2 pt-2 border-top mt-3">
        <span class="small text-muted">
          Showing {{ (currentPage - 1) * perPage + 1 }} - {{ Math.min(currentPage * perPage, filteredHistory.length) }} of {{ filteredHistory.length }}
        </span>
        <div v-if="totalPages > 1">
          <Pagination
            v-model="currentPage"
            :pages="totalPages"
            :threshold="2"
          >
            <template #previous>Prev</template>
            <template #next>Next</template>
          </Pagination>
        </div>
      </div>
    </div>
  </Card>
</template>

<script>
import Card from '@/components/card/Card.vue';
import notify from '@/utils/notify';
import axios from 'axios';

export default {
  name: 'ProductHistory',
  components: {
    Card,
  },
  data() {
    return {
      storeId: null,
      productId: null,
      productName: '',
      history: [],
      summary: null,
      loading: false,
      viewMode: 'table',
      searchQuery: '',
      typeFilter: '',
      currentPage: 1,
      perPage: 25,
    };
  },
  computed: {
    sortedHistory() {
      return Array.isArray(this.history) ? [...this.history].reverse() : [];
    },
    filteredHistory() {
      let items = this.sortedHistory;

      if (this.typeFilter) {
        if (this.typeFilter === 'store_transfer') {
          items = items.filter(e => e.type === 'store_transfer_in' || e.type === 'store_transfer_out');
        } else if (this.typeFilter === 'transfer') {
          items = items.filter(e => e.type === 'transfer_in' || e.type === 'transfer_out');
        } else {
          items = items.filter(e => e.type === this.typeFilter);
        }
      }

      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase();
        items = items.filter(e => {
          const ref = (e.reference_number || e.reference || '').toLowerCase();
          const desc = (e.description || e.notes || '').toLowerCase();
          const type = (e.type || '').toLowerCase();
          return ref.includes(q) || desc.includes(q) || type.includes(q);
        });
      }

      return items;
    },
    totalPages() {
      return Math.ceil(this.filteredHistory.length / this.perPage) || 1;
    },
    paginatedHistory() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.filteredHistory.slice(start, start + this.perPage);
    },
  },
  watch: {
    searchQuery() {
      this.currentPage = 1;
    },
    typeFilter() {
      this.currentPage = 1;
    },
    perPage() {
      this.currentPage = 1;
    },
  },
  mounted() {
    this.storeId = this.$route.params.storeId;
    this.productId = this.$route.params.productId;
    this.productName = this.$route.query.productName || 'Product';
    this.loadHistory();
  },
  methods: {
    getApiUrl(path) {
      const baseUrl = (process.env.VUE_APP_API_URL || '').replace(/\/+$/, '');
      const cleanPath = path.replace(/^\/+/, '');
      return `${baseUrl}/${cleanPath}`;
    },

    getHeaders() {
      const token = sessionStorage.getItem('access_token');
      return {
        Authorization: 'Bearer ' + token,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      };
    },

    async loadHistory() {
      if (!this.storeId || !this.productId) return;
      this.loading = true;
      try {
        const historyResponse = await axios.get(
          this.getApiUrl(`store/${this.storeId}/product/${this.productId}/history`),
          { headers: this.getHeaders() }
        );
        const data = historyResponse.data;
        this.history = Array.isArray(data) ? data : (data?.history || []);

        const summaryResponse = await axios.get(
          this.getApiUrl(`store/${this.storeId}/product/${this.productId}/history-summary`),
          { headers: this.getHeaders() }
        );
        this.summary = summaryResponse.data || {};
      } catch (error) {
        console.error('Error loading product history:', error);
        notify.alert.error('Failed to load product history');
      } finally {
        this.loading = false;
      }
    },

    getTypeLabel(type) {
      const labels = {
        purchase: 'Purchase',
        direct_purchase: 'Direct Purchase',
        sale: 'Sales',
        sale_return: 'Sale Return',
        supplier_return: 'Supplier Return',
        store_transfer_out: 'Store Transfer Out',
        store_transfer_in: 'Store Transfer In',
        transfer_out: 'Warehouse Out',
        transfer_in: 'Warehouse In',
      };
      return labels[type] || type;
    },

    getTypeIcon(type) {
      const icons = {
        purchase: 'bi bi-bag-plus',
        direct_purchase: 'bi bi-bag-check',
        sale: 'bi bi-cart-check',
        sale_return: 'bi bi-arrow-counterclockwise',
        supplier_return: 'bi bi-box-seam',
        store_transfer_out: 'bi bi-arrow-right',
        store_transfer_in: 'bi bi-arrow-left',
        transfer_out: 'bi bi-arrow-right',
        transfer_in: 'bi bi-arrow-left',
      };
      return icons[type] || 'bi bi-dot';
    },

    getTypeClass(type) {
      const classes = {
        purchase: 'bg-primary text-white',
        direct_purchase: 'bg-info text-white',
        sale: 'bg-success text-white',
        sale_return: 'bg-warning text-dark',
        supplier_return: 'bg-danger text-white',
        store_transfer_out: 'bg-secondary text-white',
        store_transfer_in: 'bg-secondary text-white',
        transfer_out: 'bg-secondary text-white',
        transfer_in: 'bg-secondary text-white',
      };
      return classes[type] || 'bg-secondary text-white';
    },

    getTypeBadgeClass(type) {
      const classes = {
        purchase: 'bg-primary',
        direct_purchase: 'bg-info',
        sale: 'bg-success',
        sale_return: 'bg-warning text-dark',
        supplier_return: 'bg-danger',
        store_transfer_out: 'bg-secondary',
        store_transfer_in: 'bg-secondary',
        transfer_out: 'bg-secondary',
        transfer_in: 'bg-secondary',
      };
      return classes[type] || 'bg-secondary';
    },

    formatCompactDate(date) {
      if (!date) return '-';
      const d = new Date(date);
      return d.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    },

    formatCurrency(value) {
      if (!value) return 'KES 0';
      return new Intl.NumberFormat('en-KE', {
        style: 'currency',
        currency: 'KES',
        maximumFractionDigits: 0,
      }).format(value);
    },
  },
};
</script>

<style scoped>
.compact-ledger table {
  font-size: 0.85rem;
}

.compact-ledger td,
.compact-ledger th {
  padding: 0.45rem 0.6rem;
}

.timeline-dot {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  flex-shrink: 0;
}

.shadow-2xs {
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.bg-secondary {
  background-color: #6c757d;
}
</style>
